import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, Heart, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { LOGO_URL, waLink } from "@/lib/brand";
import { useApp } from "@/context/AppState";

export default function Header({ onValuation }) {
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const { favorites } = useApp();
  const onHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled || !onHome;

  const nav = [
    { label: "Vendere", to: "/" },
    { label: "Immobili", to: "/immobili" },
    { label: "Casa Crush", to: "/casa-crush" },
    { label: "Mutuo", to: "/mutuo" },
    { label: "Cambio Casa", to: "/cambio-casa" },
    { label: "Vacanze", to: "/vacanze" },
  ];

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        solid ? "bg-warm/85 backdrop-blur-xl border-b border-border-subtle" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto max-w-[1400px] pl-4 pr-5 sm:px-6 lg:px-12 h-20 sm:h-24 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center min-w-0 shrink" data-testid="header-logo">
          <div className="h-14 sm:h-[80px] w-[170px] min-[400px]:w-[190px] sm:w-[280px] flex items-center">
            <img src={LOGO_URL} alt="Casamore Servizi Immobiliari" className="w-full h-full object-contain object-left" />
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {nav.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              className={`text-sm font-semibold tracking-wide transition-colors whitespace-nowrap ${
                pathname === n.to ? "text-brand" : "text-anthracite/70 hover:text-brand"
              }`}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {n.label === "Immobili" ? "IMMOBILI" : n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {favorites.length > 0 && (
            <Link to="/immobili" className="relative hidden sm:grid place-items-center h-10 w-10 rounded-full hover:bg-accent-subtle transition-colors" data-testid="header-favorites" aria-label="Preferiti">
              <Heart className="h-5 w-5 text-brand" />
              <span className="absolute -top-0.5 -right-0.5 grid place-items-center h-4 w-4 rounded-full bg-brand text-white text-[10px] font-bold">{favorites.length}</span>
            </Link>
          )}
          <a
            href={waLink("Salve Pierluigi, vorrei alcune informazioni.")}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-brand hover:text-brand-hover transition-colors"
            data-testid="header-whatsapp"
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp
          </a>
          <button
            onClick={onValuation}
            className="rounded-full bg-brand text-white text-xs sm:text-sm font-semibold px-4 sm:px-5 py-2.5 whitespace-nowrap transition-transform hover:scale-[1.04] hover:bg-brand-hover"
            data-testid="header-valuation-cta"
          >
            Valuta ora
          </button>

          {/* Mobile menu */}
          <Sheet>
            <SheetTrigger asChild>
              <button className="lg:hidden grid place-items-center h-11 w-11 rounded-full border border-border-subtle bg-white/80 hover:bg-accent-subtle transition-colors" data-testid="mobile-menu-trigger" aria-label="Menu">
                <Menu className="h-5 w-5 text-anthracite" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-warm w-[80vw] max-w-xs p-6">
              <SheetTitle className="sr-only">Menu di navigazione</SheetTitle>
              <SheetDescription className="sr-only">Collegamenti alle sezioni del sito Casamore</SheetDescription>
              <nav className="mt-8 flex flex-col gap-1">
                {nav.map((n) => (
                  <Link key={n.to} to={n.to} className={`rounded-xl px-4 py-3 text-base font-semibold transition-colors ${pathname === n.to ? "bg-brand text-white" : "text-anthracite hover:bg-accent-subtle"}`} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    {n.label}
                  </Link>
                ))}
                <a href={waLink("Salve Pierluigi, vorrei alcune informazioni.")} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-2 rounded-xl px-4 py-3 text-base font-semibold text-brand" data-testid="mobile-nav-whatsapp">
                  <MessageCircle className="h-5 w-5" /> WhatsApp
                </a>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </motion.header>
  );
}
